import { useEffect } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useAuth } from "../auth/context/AuthContext";
import { retryAfterMs } from "../../lib/apiRetry";
import {
  emitRealtimeEvent,
  setRealtimeConnectionState,
  type RealtimeEvent,
} from "./realtimeEvents";

const BACKGROUND_QUERY_KEYS: Record<string, QueryKey[]> = {
  "mockup-generation": [["mockup-generation"], ["mockups"], ["downloads"]],
  "template-batch": [["templates"], ["downloads"]],
  "design-asset-processing": [["design-assets"], ["design-jobs"]],
  gs1: [
    ["gs1-connection"],
    ["gtin-pool-status"],
    ["gtin-pool"],
    ["gs1-gcp-overview"],
    ["articles", "images-status"],
    ["listing-drafts"],
  ],
  "article-images": [["articles", "images-status"], ["listing-drafts"]],
  "article-images-reconcile": [["articles", "images-status"], ["listing-drafts"]],
  "amazon-orders": [["sales-stats"], ["orders"], ["returns"], ["customers"], ["shipping"]],
  messages: [["messages"], ["returns"], ["orders"], ["customers"]],
  "marketplace-actions": [["marketplace-actions"]],
  "marketplace-sync": [
    ["sales-stats"],
    ["marketplace-connections"],
    ["orders"],
    ["returns"],
    ["shipping"],
  ],
  fulfillment: [["sales-stats"], ["bulk-ship"], ["orders"], ["shipping"]],
  "advertising-sync": [["advertising"]],
  "advertising-actions": [["advertising"]],
};

const COMMON_BACKGROUND_KEYS: QueryKey[] = [
  ["jobs-overview"],
  ["notification-badges"],
  ["dashboard"],
];

const LIVE_PROGRESS_KEYS: QueryKey[] = [["jobs-overview"]];
const TERMINAL_JOB_STATUSES = new Set(["completed", "failed"]);

export const REALTIME_STREAM_WATCHDOG_MS = 55_000;
export const REALTIME_STABLE_CONNECTION_MS = 30_000;
const DOMAIN_INVALIDATION_DELAY_MS = 1_500;
const PROGRESS_INVALIDATION_DELAY_MS = 2_500;

export class RealtimeRateLimitError extends Error {
  constructor(readonly retryMs: number) {
    super("Realtime stream rate limited");
  }
}

function isDomainTransition(event: RealtimeEvent): boolean {
  const status = String(event.payload.status || "");
  if (TERMINAL_JOB_STATUSES.has(status)) return true;
  if (event.type === "listing-draft.updated")
    return ["review", "created", "cancelled"].includes(status);
  if (event.type === "design.job.updated") return ["released", "cancelled"].includes(status);
  return false;
}

function commonKeysForEvent(event: RealtimeEvent): QueryKey[] {
  return isDomainTransition(event) ? COMMON_BACKGROUND_KEYS : LIVE_PROGRESS_KEYS;
}

function progressKeysForEvent(event: RealtimeEvent): QueryKey[] {
  if (!event.entityId) return [];
  switch (event.type) {
    case "design.job.updated":
      return [
        ["design-jobs", "detail", event.entityId],
        ["design-jobs", "results", event.entityId],
        ["design-jobs", "review-count"],
      ];
    case "listing-draft.updated":
      return [
        ["listing-drafts", "detail", event.entityId],
        ["listing-drafts", "count"],
      ];
    case "amazon-import.updated":
      return [["amazon-import", "run", event.entityId]];
    default:
      return [];
  }
}

const ALL_REALTIME_KEYS: QueryKey[] = [
  ["shipment-reminders"],
  ["design-jobs"],
  ["listing-drafts"],
  ["amazon-import"],
  ["marketplace-push-status"],
  ["marketplace-push-status-page"],
  ["etsy-rate-budget"],
  ["amazon-policy-cases"],
  ["amazon-protection-article"],
  ["marketplace-protection-check"],
  ["marketplace-connections"],
  ["master-data"],
  ["credit-notes"],
  ...COMMON_BACKGROUND_KEYS,
  ...Object.values(BACKGROUND_QUERY_KEYS).flat(),
];

export function realtimeEventsUrl(serverUrl = import.meta.env.VITE_SERVER_URL): string {
  const base = String(serverUrl || "").replace(/\/$/, "");
  return `${base}/api/events`;
}

export function invalidationKeysForRealtimeEvent(event: RealtimeEvent): QueryKey[] {
  if (event.type === "shipment-reminder.updated")
    return [["shipment-reminders"], ["messages"], ["notification-badges"]];
  const commonKeys = commonKeysForEvent(event);
  const terminal = isDomainTransition(event);
  if (event.type !== "stream.ready" && !terminal) {
    return [...progressKeysForEvent(event), ...commonKeys];
  }
  switch (event.type) {
    case "stream.ready":
      return [];
    case "design.job.updated":
      return [["design-jobs"], ["downloads"], ...commonKeys];
    case "listing-draft.updated":
      return [["listing-drafts"], ["articles"], ...commonKeys];
    case "amazon-import.updated":
      return [["amazon-import"], ["articles"], ...commonKeys];
    case "marketplace.push.updated": {
      const queue = typeof event.payload.queue === "string" ? event.payload.queue : "";
      if (
        queue === "amazon-feeds" &&
        ["order-refund", "order-refund-poll"].includes(String(event.payload.jobName || ""))
      ) {
        return [
          ["orders"],
          ["returns"],
          ["messages"],
          ["customers"],
          ["credit-notes"],
          ...commonKeys,
        ];
      }
      const etsyBudgetKeys = queue.startsWith("etsy-") ? [["etsy-rate-budget"]] : [];
      return [
        ["marketplace-push-status"],
        ["marketplace-push-status-page"],
        ["marketplace-connections"],
        ["articles"],
        ["amazon-protection"],
        ["amazon-protection-article"],
        ["marketplace-protection-check"],
        ["amazon-policy-cases"],
        ...etsyBudgetKeys,
        ...commonKeys,
      ];
    }
    case "background-job.updated": {
      const queue = typeof event.payload.queue === "string" ? event.payload.queue : "";
      const marketplaceOrderKeys = /-(orders|operations)$/.test(queue)
        ? [
            ["sales-stats"],
            ["marketplace-connections"],
            ["orders"],
            ["returns"],
            ["messages"],
            ["shipping"],
            ["master-data"],
          ]
        : [];
      const etsyBudgetKeys = queue.startsWith("etsy-") ? [["etsy-rate-budget"]] : [];
      return [
        ...(BACKGROUND_QUERY_KEYS[queue] ?? marketplaceOrderKeys),
        ...etsyBudgetKeys,
        ...commonKeys,
      ];
    }
    default:
      return [];
  }
}

export function parseSseBlock(block: string): RealtimeEvent | null {
  const data = block
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n");
  if (!data) return null;
  try {
    const event = JSON.parse(data) as Partial<RealtimeEvent>;
    if (
      typeof event.id !== "string" ||
      typeof event.type !== "string" ||
      typeof event.occurredAt !== "string" ||
      typeof event.entityType !== "string" ||
      typeof event.entityId !== "string" ||
      !event.payload ||
      typeof event.payload !== "object"
    ) {
      return null;
    }
    return event as RealtimeEvent;
  } catch {
    return null;
  }
}

export async function consumeStream(
  signal: AbortSignal,
  onEvent: (event: RealtimeEvent) => void,
  onActivity: () => void = () => undefined,
): Promise<"closed" | "unauthorized"> {
  const response = await fetch(realtimeEventsUrl(), {
    method: "GET",
    credentials: "include",
    headers: { Accept: "text/event-stream" },
    cache: "no-store",
    signal,
  });
  if (response.status === 401 || response.status === 403) {
    await response.body?.cancel().catch(() => undefined);
    return "unauthorized";
  }
  if (!response.ok || !response.body) {
    await response.body?.cancel().catch(() => undefined);
    if (response.status === 429) {
      throw new RealtimeRateLimitError(retryAfterMs(response.headers.get("Retry-After")) ?? 60_000);
    }
    throw new Error(`Realtime stream unavailable (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (!signal.aborted) {
      let watchdog: number | null = null;
      const read = reader.read();
      const timedRead = new Promise<never>((_resolve, reject) => {
        watchdog = window.setTimeout(() => {
          void reader.cancel("realtime-watchdog");
          reject(new Error("Realtime stream heartbeat timed out"));
        }, REALTIME_STREAM_WATCHDOG_MS);
      });
      const { value, done } = await Promise.race([read, timedRead]).finally(() => {
        if (watchdog !== null) window.clearTimeout(watchdog);
      });
      if (value?.byteLength) onActivity();
      buffer += decoder.decode(value, { stream: !done });
      let boundary = buffer.search(/\r?\n\r?\n/);
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        const separator = buffer.slice(boundary).match(/^\r?\n\r?\n/)?.[0] ?? "\n\n";
        buffer = buffer.slice(boundary + separator.length);
        const event = parseSseBlock(block);
        if (event) onEvent(event);
        boundary = buffer.search(/\r?\n\r?\n/);
      }
      if (done) return "closed";
    }
    return "closed";
  } finally {
    reader.releaseLock();
  }
}

export function reconnectDelay(attempt: number, random = Math.random): number {
  const base = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
  return Math.round(base * (0.7 + random() * 0.6));
}

export function shouldReconnectRealtimeStream(
  streamReadyAt: number,
  lastByteAt: number,
  now = Date.now(),
): boolean {
  return streamReadyAt <= 0 || lastByteAt <= 0 || now - lastByteAt >= REALTIME_STREAM_WATCHDOG_MS;
}

export default function RealtimeEventsBridge() {
  const { isAuthenticated, isAuthLoading, supportSession } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated || supportSession) return undefined;
    const controller = new AbortController();
    const pendingDomainInvalidations = new Map<string, QueryKey>();
    const pendingProgressInvalidations = new Map<string, QueryKey>();
    let domainInvalidationTimer: number | null = null;
    let progressInvalidationTimer: number | null = null;
    let activeStreamController: AbortController | null = null;
    let wakeReconnectWait: (() => void) | null = null;
    let reconnectRequested = false;
    let currentStreamReadyAt = 0;
    let lastStreamByteAt = 0;

    const flushInvalidations = (kind: "domain" | "progress") => {
      const pending = kind === "domain" ? pendingDomainInvalidations : pendingProgressInvalidations;
      if (kind === "domain") domainInvalidationTimer = null;
      else progressInvalidationTimer = null;
      const keys = [...pending.values()];
      pending.clear();
      for (const queryKey of keys) {
        void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
      }
    };

    const onEvent = (event: RealtimeEvent) => {
      if (event.type === "stream.ready") {
        currentStreamReadyAt = Date.now();
        lastStreamByteAt = currentStreamReadyAt;
        setRealtimeConnectionState("connected");
        // Include the initial handshake: the first REST response can predate
        // stream subscription, so a completion in that gap must be reconciled.
        for (const queryKey of ALL_REALTIME_KEYS) {
          pendingDomainInvalidations.set(JSON.stringify(queryKey), queryKey);
        }
      }
      emitRealtimeEvent(event);
      for (const queryKey of invalidationKeysForRealtimeEvent(event)) {
        const progressOnly = queryKey[0] === "jobs-overview";
        const pending = progressOnly ? pendingProgressInvalidations : pendingDomainInvalidations;
        pending.set(JSON.stringify(queryKey), queryKey);
      }
      if (pendingDomainInvalidations.size > 0 && domainInvalidationTimer === null) {
        domainInvalidationTimer = window.setTimeout(
          () => flushInvalidations("domain"),
          DOMAIN_INVALIDATION_DELAY_MS,
        );
      }
      if (pendingProgressInvalidations.size > 0 && progressInvalidationTimer === null) {
        progressInvalidationTimer = window.setTimeout(
          () => flushInvalidations("progress"),
          PROGRESS_INVALIDATION_DELAY_MS,
        );
      }
    };

    const requestReconnect = () => {
      if (controller.signal.aborted) return;
      if (!shouldReconnectRealtimeStream(currentStreamReadyAt, lastStreamByteAt)) return;
      reconnectRequested = true;
      activeStreamController?.abort();
      wakeReconnectWait?.();
    };

    const onOnline = () => requestReconnect();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") requestReconnect();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibilityChange);

    const waitForReconnect = (ms: number) =>
      new Promise<void>((resolve) => {
        if (controller.signal.aborted) return resolve();
        const finish = () => {
          window.clearTimeout(timeout);
          controller.signal.removeEventListener("abort", finish);
          if (wakeReconnectWait === finish) wakeReconnectWait = null;
          resolve();
        };
        const timeout = window.setTimeout(finish, ms);
        wakeReconnectWait = finish;
        controller.signal.addEventListener("abort", finish, { once: true });
      });

    const run = async () => {
      let attempt = 0;
      let rateLimitedUntil = 0;
      while (!controller.signal.aborted) {
        if (rateLimitedUntil > Date.now()) {
          await waitForReconnect(rateLimitedUntil - Date.now());
          continue;
        }
        setRealtimeConnectionState(attempt === 0 ? "connecting" : "disconnected");
        currentStreamReadyAt = 0;
        lastStreamByteAt = 0;
        const streamController = new AbortController();
        activeStreamController = streamController;
        const abortStream = () => streamController.abort();
        controller.signal.addEventListener("abort", abortStream, { once: true });
        try {
          const result = await consumeStream(streamController.signal, onEvent, () => {
            lastStreamByteAt = Date.now();
          });
          if (result === "unauthorized" || controller.signal.aborted) {
            setRealtimeConnectionState("disconnected");
            return;
          }
        } catch (error) {
          if (controller.signal.aborted) return;
          if (error instanceof RealtimeRateLimitError)
            rateLimitedUntil = Date.now() + error.retryMs;
        } finally {
          controller.signal.removeEventListener("abort", abortStream);
          if (activeStreamController === streamController) activeStreamController = null;
        }
        if (
          currentStreamReadyAt > 0 &&
          Date.now() - currentStreamReadyAt >= REALTIME_STABLE_CONNECTION_MS
        ) {
          attempt = 0;
        }
        setRealtimeConnectionState("disconnected");
        if (reconnectRequested) {
          reconnectRequested = false;
          continue;
        }
        await waitForReconnect(reconnectDelay(attempt));
        reconnectRequested = false;
        attempt += 1;
      }
    };

    void run();
    return () => {
      controller.abort();
      activeStreamController?.abort();
      wakeReconnectWait?.();
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      setRealtimeConnectionState("disconnected");
      if (domainInvalidationTimer !== null) window.clearTimeout(domainInvalidationTimer);
      if (progressInvalidationTimer !== null) window.clearTimeout(progressInvalidationTimer);
      pendingDomainInvalidations.clear();
      pendingProgressInvalidations.clear();
    };
  }, [isAuthenticated, isAuthLoading, supportSession, queryClient]);

  return null;
}
